import * as React from 'react';
import { IconButton, Icon } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Container from 'common/components/Container';
import Fab from '@mui/material/Fab';
import { ReactComponent as ExpandIcon } from 'common/assets/icons/icon-expand-outline.svg';
import { ReactComponent as CollapseIcon } from 'common/assets/icons/icon-collapse-outline.svg';
import CompletedViewPreviewDialog from './CompletedViewPreviewDialog';

interface OwnProps {
	thankYouText: string;
	nextStepsText: string;
	endBtnText: string;
	scale: number;
	button?: 'expandButton' | 'collapseButton';
	buttonSize?: number;
	handleCloseDialog?: () => void;
	infoText?: string;
	isHelpText?: boolean;
}

const useStyles = makeStyles((theme) => ({
	root: {
		position: 'relative',
		backgroundColor: theme.palette.background.default,
		'-webkit-transform-origin': 'top left',
	},
	textField: {
		width: '100%',
	},
	infoText: {
		marginTop: 36,
		fontWeight: 400,
	},
	stepsWrapper: {
		textAlign: 'center',
	},
	highlightText: {
		fontSize: '3.8rem',
	},
	checkBoxWrapper: {
		marginTop: 32,
	},
	controlLabel: {
		margin: '0 auto',
		maxWidth: 350,
	},
	header: {
		marginBottom: 48,
	},
	endBtnWrapper: {
		position: 'relative',
		maxWidth: 300,
		margin: 'auto',
		marginTop: 80,
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	btn: {
		width: '100%',
		height: '100%',
		padding: '16px 64px',
		pointerEvents: 'none',
	},
	expandButton: {
		position: 'absolute',
		right: 32,
		top: 32,
	},
}));

type Props = OwnProps;

const CompletedViewPreview: React.FC<Props> = (props) => {
	const [previewDialogOpen, setPreviewDialogOpen] = React.useState<boolean>(false);
	const classes = useStyles();
	const {
		thankYouText,
		nextStepsText,
		endBtnText,
		scale,
		button,
		buttonSize,
		handleCloseDialog,
		infoText,
		isHelpText,
	} = props;

	const handlePreviewDialogOpen = () => {
		setPreviewDialogOpen(true);
	};

	const handlePreviewDialogClose = () => {
		setPreviewDialogOpen(false);
	};

	return (
		<React.Fragment>
			<Container
				className={classes.root}
				style={{ transform: `scale(${scale})`, width: `calc(100% / ${scale})` }}
			>
				{button === 'expandButton' ? (
					<IconButton
						className={classes.expandButton}
						onClick={handlePreviewDialogOpen}
						size="large"
					>
						<Icon style={{ fontSize: buttonSize }}>
							<ExpandIcon />
						</Icon>
					</IconButton>
				) : button === 'collapseButton' ? (
					<IconButton className={classes.expandButton} onClick={handleCloseDialog} size="large">
						<Icon style={{ fontSize: buttonSize }}>
							<CollapseIcon />
						</Icon>
					</IconButton>
				) : null}
				<div className={classes.stepsWrapper}>
					<Typography key="text3" gutterBottom variant="h4" className={classes.header}>
						{thankYouText}
					</Typography>
					<Typography key="text4" gutterBottom variant="h4">
						{nextStepsText}
					</Typography>
				</div>
				{infoText && (
					<Typography
						gutterBottom
						variant={isHelpText ? 'h6' : 'h4'}
						color={isHelpText ? 'textSecondary' : 'textPrimary'}
						align="center"
						className={classes.infoText}
					>
						{infoText}
					</Typography>
				)}
				<div className={classes.endBtnWrapper}>
					<Fab className={classes.btn} variant="extended" color="primary">
						<Typography variant="h4" color="inherit">
							{endBtnText}
						</Typography>
					</Fab>
				</div>
			</Container>
			<CompletedViewPreviewDialog
				open={previewDialogOpen}
				onClose={handlePreviewDialogClose}
				completeViewProps={props}
			/>
		</React.Fragment>
	);
};

export default CompletedViewPreview;
